import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Users, Lock, AlertCircle, ArrowLeft, Info } from 'lucide-react';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { sendOtp, verifyOtp, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Navigate to dashboard when authentication is successful
  useEffect(() => {
    if (isAuthenticated) {
      console.log('✅ User authenticated, navigating to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handlePhoneChange = (value) => {
    setPhone(value);
    if (value) {
      const phoneNumber = parsePhoneNumber(value);
      if (phoneNumber) {
        setDialCode(phoneNumber.countryCallingCode);
        setCountryName(phoneNumber.country);
      }
    }
  };

  const handleSendOtp = async () => {
    if (!phone) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Extract country code and mobile number separately
      const phoneNumber = parsePhoneNumber(phone);
      if (!phoneNumber) {
        setError('Please enter a valid phone number');
        setLoading(false);
        return;
      }

      const countryCode = phoneNumber.countryCallingCode;
      const mobileNumber = phoneNumber.nationalNumber;

      console.log('📱 Sending OTP with:', { countryCode, mobileNumber, fullPhone: phone });

      const success = await sendOtp(countryCode, mobileNumber);
      if (success) {
        setShowOtp(true);
        setError('');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      
      // Check if it's a phone number not found error
      if (error.response?.data?.error === 'PHONE_NOT_FOUND') {
        setError(
          <div className="text-left">
            <p className="font-medium text-red-800 mb-2">Phone number not found in our records</p>
            <p className="text-sm text-red-700">
              <strong>Note:</strong> Make sure you are logging in with the same number used while making the enquiry form or call on <strong>080-47092273</strong>
            </p>
          </div>
        );
      } else {
        setError(error.response?.data?.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Extract country code and mobile number separately
      const phoneNumber = parsePhoneNumber(phone);
      if (!phoneNumber) {
        setError('Please enter a valid phone number');
        setLoading(false);
        return;
      }

      const countryCode = phoneNumber.countryCallingCode;
      const mobileNumber = phoneNumber.nationalNumber;

      console.log('🔍 Verifying OTP for:', { countryCode, mobileNumber, fullPhone: phone });
      const result = await verifyOtp(countryCode, mobileNumber, otp);
      console.log('🔍 OTP verification result:', result);
      
      if (result.success) {
        console.log('✅ OTP verified successfully, waiting for auth state update');
        // Don't navigate here - let the useEffect handle it when isAuthenticated becomes true
      } else {
        console.log('❌ OTP verification failed');
        setError('Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      
      // Handle specific error cases
      if (error.response?.data?.error === 'OTP_SESSION_EXPIRED') {
        setError(
          <div className="text-left">
            <p className="font-medium text-red-800 mb-2">OTP Session Expired</p>
            <p className="text-sm text-red-700">
              Your OTP session has expired. This can happen if the server was restarted or after a period of inactivity.
            </p>
            <p className="text-sm text-red-700 mt-2">
              Please request a new OTP by clicking "Send OTP" again.
            </p>
          </div>
        );
        // Reset to phone input screen
        setShowOtp(false);
        setOtp('');
      } else if (error.response?.data?.error === 'OTP_EXPIRED') {
        setError(
          <div className="text-left">
            <p className="font-medium text-red-800 mb-2">OTP Expired</p>
            <p className="text-sm text-red-700">
              Your OTP has expired. OTPs are valid for 10 minutes only.
            </p>
            <p className="text-sm text-red-700 mt-2">
              Please request a new OTP by clicking "Resend OTP".
            </p>
          </div>
        );
      } else {
        setError(error.response?.data?.message || 'Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/images/Logos/tac-logo.png" 
              alt="Academic City Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="bg-teal-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Users className="h-8 w-8 text-teal-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Parent Login</h2>
          <p className="text-gray-600">Access your child's application portal</p>
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">

          {!showOtp ? (
            // Phone Number Input
            <div className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Number
                </label>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="IN"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="form-input"
                  placeholder="Enter your WhatsApp number"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the number which you have entered while doing enquiry. You will receive the OTP on WhatsApp
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Send OTP Button */}
              <button
                onClick={handleSendOtp}
                disabled={loading || !phone}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending OTP...
                  </div>
                ) : (
                  'Send OTP'
                )}
              </button>
            </div>
          ) : (
            // OTP Input
            <div className="space-y-6">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  OTP
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="form-input pl-12"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the 6-digit code sent to {phone}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Verify OTP Button */}
              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify OTP'
                )}
              </button>

              {/* Resend OTP */}
              <div className="text-center">
                <button
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium disabled:opacity-50"
                >
                  Resend OTP
                </button>
              </div>

              {/* Change Phone Number */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowOtp(false);
                    setOtp('');
                    setError('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                >
                  Change Phone Number
                </button>
              </div>
            </div>
          )}

          {/* Additional Links */}
          <div className="mt-6 text-center space-y-3">
            {/* <div className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Start Application
              </Link>
            </div> */}
          </div>
        </div>

        {/* Back to Login Selection */}
        <div className="text-center">
          <Link
            to="/login"
            className="text-teal-600 hover:text-teal-700 font-medium transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login Options
          </Link>
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <Info className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-800">Need Help?</span>
          </div>
          <p className="text-sm text-blue-700">
            Facing difficulties? Call on the number{' '}
            <a 
              href="tel:080-47092273" 
              className="font-semibold text-blue-800 hover:text-blue-900 underline"
            >
              080-47092273
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
